import { Eye, Info, Mail, UserCheck } from "lucide-react";
import { useMemo } from "react";
import { CreateOfferDialog } from "@/components/create-offer-dialog";
import { InterviewSchedulingSection } from "@/components/interview-scheduling-section";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchableSelect } from "@/components/ui/searchable-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import type { UseMoveApplicationReturn } from "@/hooks/use-move-application";
import { DEPARTMENTS, EMPLOYMENT_TYPES } from "@/lib/constants";
import { queryClient } from "@/lib/queryClient";
import { orpc } from "@/lib/rpc";
import type { PersonOption, StageInterviewersData } from "@/types/move-application";

interface MoveConfirmationDialogProps {
  moveState: UseMoveApplicationReturn;
  people?: PersonOption[];
  stageInterviewers?: StageInterviewersData;
  isLoadingStageInterviewers?: boolean;
  jobId?: string;
  jobTitle: string;
  jobLevel?: string;
}

export function MoveConfirmationDialog({
  moveState,
  people,
  isLoadingStageInterviewers = false,
  jobId,
  jobTitle,
  jobLevel,
}: MoveConfirmationDialogProps) {
  const {
    pendingMove,
    isConfirmOpen,
    interviewerId,
    setInterviewerId,
    selectedGroupId,
    setSelectedGroupId,
    assignmentMode,
    setAssignmentMode,
    createNewRecord,
    setCreateNewRecord,
    sendEmailToCandidate,
    setSendEmailToCandidate,
    emailStageName,
    setEmailStageName,
    emailSubject,
    setEmailSubject,
    emailIntroText,
    setEmailIntroText,
    emailExtraBody,
    setEmailExtraBody,
    emailButtonText,
    setEmailButtonText,
    emailClosingText,
    setEmailClosingText,
    currentStepOwnerId,
    setCurrentStepOwnerId,
    rejectionReason,
    setRejectionReason,
    sendRejectionFeedback,
    setSendRejectionFeedback,
    rejectionFeedbackSubject,
    setRejectionFeedbackSubject,
    rejectionFeedbackBody,
    setRejectionFeedbackBody,
    showRejectionPreview,
    setShowRejectionPreview,
    isRejectionMove,
    isHireMove,
    needsOfferForm,
    canConfirmMove,
    isConfirmSubmitting,
    confirmMove,
    cancelMove,
    resetMoveState,
    buildAssignmentOptions,
    resolvedHiringManagerFullName,
    isCreateOfferDialogOpen,
    setIsCreateOfferDialogOpen,
    offerDialogApplicationId,
    offerDialogApplicantName,
    hireFullName,
    setHireFullName,
    hirePersonalEmail,
    setHirePersonalEmail,
    hirePhone,
    setHirePhone,
    hireEmploymentType,
    setHireEmploymentType,
    hireJobTitle,
    setHireJobTitle,
    hireJobLevel,
    setHireJobLevel,
    hireDepartment,
    setHireDepartment,
    hireStartDate,
    setHireStartDate,
    hireInitialSalary,
    setHireInitialSalary,
  } = moveState;

  const dialogAssignmentOptions = useMemo(() => {
    if (!pendingMove?.targetInterviewStageId) return [];
    return buildAssignmentOptions(pendingMove.targetInterviewStageId);
  }, [pendingMove?.targetInterviewStageId, buildAssignmentOptions]);

  return (
    <>
      <Dialog open={isConfirmOpen} onOpenChange={(open) => !open && cancelMove()}>
        <DialogContent className={isRejectionMove || isHireMove ? "max-w-lg" : ""}>
          <DialogHeader>
            <DialogTitle>
              {isHireMove
                ? "Registrar contratação"
                : isRejectionMove
                  ? "Rejeitar candidato"
                  : "Confirmar movimentação"}
            </DialogTitle>
            <DialogDescription>
              {isHireMove ? (
                <>
                  Sem oferta aceita no sistema. Preencha os dados abaixo para criar{" "}
                  <strong>{pendingMove?.applicantName}</strong> como colaborador.
                </>
              ) : isRejectionMove ? (
                <>
                  Rejeitar <strong>{pendingMove?.applicantName}</strong> do processo seletivo.
                </>
              ) : (
                <>
                  Deseja mover <strong>{pendingMove?.applicantName}</strong> para a etapa{" "}
                  <strong>{pendingMove?.targetColumnTitle}</strong>?
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          {/* Validation warnings */}
          {pendingMove?.validation?.warnings && pendingMove.validation.warnings.length > 0 && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3 space-y-2">
              {pendingMove.validation.warnings.map((warning) => (
                <p key={warning.message} className="text-sm text-yellow-800 dark:text-yellow-200">
                  {warning.message}
                </p>
              ))}
              {(pendingMove.validation.canCreateNewInterview ||
                pendingMove.validation.canCreateNewOffer) && (
                <div className="pt-2 border-t border-yellow-200 dark:border-yellow-800">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={createNewRecord}
                      onChange={(e) => setCreateNewRecord(e.target.checked)}
                      className="rounded border-yellow-400"
                      data-testid="checkbox-create-new-record"
                    />
                    <span className="text-yellow-800 dark:text-yellow-200">
                      {pendingMove.validation.canCreateNewInterview &&
                        "Criar nova entrevista para esta etapa"}
                      {pendingMove.validation.canCreateNewOffer && "Criar nova oferta"}
                    </span>
                  </label>
                </div>
              )}
            </div>
          )}

          {/* Interview stage section */}
          {pendingMove?.isInterviewStage &&
            (!pendingMove?.validation?.requiresConfirmation || createNewRecord) &&
            (isLoadingStageInterviewers ? (
              <div className="space-y-4 py-4">
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
                  <span className="ml-2 text-sm text-muted-foreground">
                    Carregando entrevistadores...
                  </span>
                </div>
              </div>
            ) : (
              <>
                <InterviewSchedulingSection
                  assignmentOptions={dialogAssignmentOptions}
                  people={people}
                  interviewerId={interviewerId}
                  setInterviewerId={setInterviewerId}
                  selectedGroupId={selectedGroupId}
                  setSelectedGroupId={setSelectedGroupId}
                  assignmentMode={assignmentMode}
                  setAssignmentMode={setAssignmentMode}
                  sendEmailToCandidate={sendEmailToCandidate}
                  setSendEmailToCandidate={setSendEmailToCandidate}
                  emailStageName={emailStageName}
                  setEmailStageName={setEmailStageName}
                  emailSubject={emailSubject}
                  setEmailSubject={setEmailSubject}
                  emailIntroText={emailIntroText}
                  setEmailIntroText={setEmailIntroText}
                  emailExtraBody={emailExtraBody}
                  setEmailExtraBody={setEmailExtraBody}
                  emailButtonText={emailButtonText}
                  setEmailButtonText={setEmailButtonText}
                  emailClosingText={emailClosingText}
                  setEmailClosingText={setEmailClosingText}
                  candidateName={pendingMove.applicantName || "Candidato(a)"}
                  durationMinutes={pendingMove.stage?.durationMinutes || 60}
                  stageDescription={pendingMove.stage?.description || ""}
                />
                {pendingMove.stage && (
                  <p className="text-sm text-muted-foreground">
                    Duração: {pendingMove.stage.durationMinutes} minutos
                  </p>
                )}
              </>
            ))}

          {/* Reference checks owner */}
          {pendingMove && pendingMove.targetColumn === "reference_checks" && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <UserCheck className="w-4 h-4" />
                Selecione o responsável por esta etapa.
              </div>
              <div className="space-y-2">
                <Label htmlFor="owner">Responsável</Label>
                <SearchableSelect
                  value={currentStepOwnerId}
                  onValueChange={setCurrentStepOwnerId}
                  options={(people || []).map((p) => ({
                    value: p.id,
                    label: p.fullName,
                  }))}
                  placeholder="Selecione o responsável..."
                  data-testid="select-owner"
                />
              </div>
            </div>
          )}

          {/* Hire form */}
          {isHireMove && (
            <div className="space-y-5 py-1">
              <div className="flex items-start gap-2.5 rounded-md border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 px-3 py-2.5">
                <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  Esse é um caminho alternativo. O fluxo padrão é criar e aceitar uma oferta.
                </p>
              </div>

              {/* IDENTIDADE */}
              <div className="space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Identidade
                </p>
                <div className="space-y-1.5">
                  <Label htmlFor="hire-full-name">Nome completo</Label>
                  <Input
                    id="hire-full-name"
                    value={hireFullName}
                    onChange={(e) => setHireFullName(e.target.value)}
                    data-testid="input-hire-full-name"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="hire-personal-email">Email pessoal</Label>
                    <Input
                      id="hire-personal-email"
                      type="email"
                      value={hirePersonalEmail}
                      onChange={(e) => setHirePersonalEmail(e.target.value)}
                      placeholder="nome@email.com"
                      data-testid="input-hire-personal-email"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="hire-phone">Telefone</Label>
                    <Input
                      id="hire-phone"
                      value={hirePhone}
                      onChange={(e) => setHirePhone(e.target.value)}
                      placeholder="(11) 99999-9999"
                      data-testid="input-hire-phone"
                    />
                  </div>
                </div>
              </div>

              {/* VÍNCULO */}
              <div className="space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Vínculo
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="hire-job-title">Cargo</Label>
                    <Input
                      id="hire-job-title"
                      value={hireJobTitle}
                      onChange={(e) => setHireJobTitle(e.target.value)}
                      data-testid="input-hire-job-title"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="hire-job-level">Nível</Label>
                    <Input
                      id="hire-job-level"
                      value={hireJobLevel}
                      onChange={(e) => setHireJobLevel(e.target.value)}
                      placeholder="L3"
                      data-testid="input-hire-job-level"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="hire-department">Departamento</Label>
                    <Select value={hireDepartment} onValueChange={setHireDepartment}>
                      <SelectTrigger id="hire-department" data-testid="select-hire-department">
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        {DEPARTMENTS.map((dept) => (
                          <SelectItem key={dept.value} value={dept.value}>
                            {dept.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="hire-employment-type">Tipo de contrato</Label>
                    <Select
                      value={hireEmploymentType}
                      onValueChange={(v) => setHireEmploymentType(v as "CLT" | "PJ")}
                    >
                      <SelectTrigger
                        id="hire-employment-type"
                        data-testid="select-hire-employment-type"
                      >
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        {EMPLOYMENT_TYPES.map((et) => (
                          <SelectItem key={et.value} value={et.value}>
                            {et.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* COMPENSAÇÃO */}
              <div className="space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Compensação
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="hire-start-date">
                      Data de início{" "}
                      <span className="text-muted-foreground font-normal">(opcional)</span>
                    </Label>
                    <Input
                      id="hire-start-date"
                      type="date"
                      value={hireStartDate}
                      onChange={(e) => setHireStartDate(e.target.value)}
                      data-testid="input-hire-start-date"
                    />
                    <p className="text-[11px] text-muted-foreground">Primeiro dia de trabalho</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="hire-initial-salary">Salário inicial</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
                        R$
                      </span>
                      <Input
                        id="hire-initial-salary"
                        value={hireInitialSalary}
                        onChange={(e) => setHireInitialSalary(e.target.value)}
                        placeholder="15.000,00"
                        className="pl-10"
                        data-testid="input-hire-initial-salary"
                      />
                    </div>
                    <p className="text-[11px] text-muted-foreground">Mensal, bruto</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Rejection form */}
          {isRejectionMove && (
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="rejection-reason" className="flex items-center gap-2">
                  Motivo da rejeição
                  <span className="text-xs font-normal text-muted-foreground bg-muted px-2 py-0.5 rounded">
                    Uso interno — não será compartilhado com o candidato
                  </span>
                </Label>
                <Textarea
                  id="rejection-reason"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Descreva o motivo interno da rejeição..."
                  rows={3}
                  data-testid="textarea-rejection-reason"
                />
              </div>

              <div className="flex items-center justify-between border rounded-md p-3">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  <Label htmlFor="send-rejection-feedback" className="font-normal cursor-pointer">
                    Enviar email de feedback ao candidato
                  </Label>
                </div>
                <Switch
                  id="send-rejection-feedback"
                  checked={sendRejectionFeedback}
                  onCheckedChange={(checked) => {
                    setSendRejectionFeedback(checked);
                    if (checked && !rejectionFeedbackBody) {
                      const candidateFirstName = (pendingMove?.applicantName || "Candidato").split(
                        " ",
                      )[0];
                      const hiringManagerFirstName =
                        resolvedHiringManagerFullName.split(" ")[0] || "Equipe de RH";
                      setRejectionFeedbackBody(
                        `Oi ${candidateFirstName},\n\nObrigado por participar do nosso processo seletivo! Infelizmente, dessa vez decidimos seguir com outros candidatos.\n\nDesejamos muito sucesso na sua jornada!\n\nAbraço,\n${hiringManagerFirstName}`,
                      );
                    }
                  }}
                  data-testid="switch-send-rejection-feedback"
                />
              </div>

              {sendRejectionFeedback && (
                <div className="space-y-3 border rounded-md p-4 bg-muted/30">
                  <div className="space-y-2">
                    <Label htmlFor="rejection-feedback-subject">Assunto do email *</Label>
                    <Input
                      id="rejection-feedback-subject"
                      value={rejectionFeedbackSubject}
                      onChange={(e) => setRejectionFeedbackSubject(e.target.value)}
                      placeholder="Atualização sobre seu processo seletivo"
                      data-testid="input-rejection-feedback-subject"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="rejection-feedback-body">Corpo do feedback *</Label>
                    <Textarea
                      id="rejection-feedback-body"
                      value={rejectionFeedbackBody}
                      onChange={(e) => setRejectionFeedbackBody(e.target.value)}
                      placeholder="Agradecemos seu interesse e participação no processo seletivo..."
                      rows={5}
                      data-testid="textarea-rejection-feedback-body"
                    />
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowRejectionPreview(!showRejectionPreview)}
                    data-testid="button-toggle-rejection-preview"
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    {showRejectionPreview ? "Ocultar prévia" : "Ver prévia do email"}
                  </Button>

                  {showRejectionPreview && (
                    <div className="border rounded-md p-4 bg-white dark:bg-gray-900 space-y-2">
                      <p className="text-xs text-muted-foreground font-medium">PRÉVIA DO EMAIL</p>
                      <p className="text-sm font-medium">
                        Assunto: {rejectionFeedbackSubject || "(sem assunto)"}
                      </p>
                      <hr />
                      <div className="text-sm space-y-2">
                        {rejectionFeedbackBody.split("\n").map((line, i) => (
                          // biome-ignore lint/suspicious/noArrayIndexKey: split lines have no stable identity and may contain duplicates
                          <p key={i}>{line || <br />}</p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={cancelMove} data-testid="button-cancel-move">
              Cancelar
            </Button>
            <Button
              onClick={confirmMove}
              disabled={!canConfirmMove || isConfirmSubmitting}
              data-testid="button-confirm-move"
            >
              {isConfirmSubmitting
                ? "Processando..."
                : isHireMove
                  ? "Confirmar contratação"
                  : needsOfferForm
                    ? "Criar Oferta"
                    : isRejectionMove
                      ? "Rejeitar"
                      : "Confirmar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CreateOfferDialog
        applicationId={offerDialogApplicationId}
        applicantName={offerDialogApplicantName}
        jobTitle={jobTitle}
        defaultJobLevel={jobLevel}
        open={isCreateOfferDialogOpen}
        onOpenChange={(open) => {
          setIsCreateOfferDialogOpen(open);
          if (!open) resetMoveState();
        }}
        onOfferCreated={() => {
          if (jobId) {
            queryClient.invalidateQueries({
              queryKey: orpc.applications.getJobKanban.queryOptions({ jobOpeningId: jobId })
                .queryKey,
            });
          }
          queryClient.invalidateQueries({
            queryKey: orpc.applications.list.queryOptions().queryKey,
          });
          queryClient.invalidateQueries({ queryKey: orpc.offers.list.queryOptions().queryKey });
          resetMoveState();
        }}
      />
    </>
  );
}
